import React, { useEffect } from "react";

import { UserLevel } from "../../../enums/userLevel";
import { useAuth } from "../../../features/auth/context";
import { OrgSelect } from "../../shared/OrgSelect";

interface Props {
    selectedDealerId: number | undefined;
    selectedOrganizationId: number | undefined;
    selectedSiteId: number | undefined;
    selectedUserLevel: UserLevel | null;
    onChange: (value: {
        dealerId?: number | null;
        orgId?: number | null;
        siteId?: number | null;
    }) => void;
    disabled?: boolean;
}

export function EmployeeOrgSelectors({
    selectedDealerId,
    selectedOrganizationId,
    selectedSiteId,
    selectedUserLevel,
    onChange,
    disabled = false,
}: Props) {
    const {
        isGlobalAdmin,
        isDealerAdmin,
        isOrgAdmin,
        isSiteAdmin,
        isSiteEmployee,
        orgId: userOrgId,
    } = useAuth();

    useEffect(() => {
        switch (selectedUserLevel) {
            case UserLevel.GlobalAdmin:
                onChange({ dealerId: null, orgId: null, siteId: null });
                break;
            case UserLevel.DealerAdmin:
            case UserLevel.DealerEmployee:
                onChange({ dealerId: selectedDealerId, orgId: null, siteId: null });
                break;
            case UserLevel.OrgAdmin:
            case UserLevel.OrgEmployee:
                // If organizationId is not set, default to the user's organization
                const orgId = selectedOrganizationId ?? userOrgId ?? null;
                onChange({ dealerId: selectedDealerId, orgId, siteId: null });
                break;
            default:
                break;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedUserLevel]);

    if (isSiteAdmin || isSiteEmployee) {
        return null;
    }

    return (
        <OrgSelect
            ignoreOrgSiteContext={true}
            selectedDealerId={selectedDealerId}
            selectedOrgId={selectedOrganizationId}
            selectedSiteId={selectedSiteId}
            dealerSelectIsEnabled={
                !disabled
                && isGlobalAdmin
                && selectedUserLevel !== null
                && selectedUserLevel !== UserLevel.GlobalAdmin
            }
            orgSelectIsEnabled={
                !disabled
                && (isGlobalAdmin || isDealerAdmin)
                && selectedUserLevel !== null
                && [
                    UserLevel.SiteAdmin,
                    UserLevel.SiteEmployee,
                    UserLevel.OrgAdmin,
                    UserLevel.OrgEmployee,
                ].includes(selectedUserLevel)
            }
            siteSelectIsEnabled={
                !disabled
                && (isGlobalAdmin || isDealerAdmin || isOrgAdmin)
                && selectedUserLevel !== null
                && [UserLevel.SiteAdmin, UserLevel.SiteEmployee].includes(selectedUserLevel)
            }
            bypassSiteContext={true}
            onChange={onChange}
            descriptive={true}
        />
    );
}
